import { useState } from 'react';
import { retrieve } from '../sections/Findy/retrieval';
import { xs, ys, tiers, tierTotals } from '../../data/imgradar-sample';
import { CANONICAL, cardTitle, parseSentence } from '../../data/chorie-grammar';
import { r407Sample, Trade } from '../../data/r407-sample';
import * as S from './styles';

export function FindyDemo() {
  const [query, setQuery] = useState('When does my insurance expire?');
  const [result, setResult] = useState(() => retrieve(query));
  const ask = (text: string) => {
    setQuery(text);
    setResult(retrieve(text));
  };
  return (
    <S.Demo aria-label="Findy interactive sample">
      <S.DemoBar>
        <span>findy / my documents</span>
        <span>8 sample files</span>
      </S.DemoBar>
      <S.DemoBody>
        <S.FileRow>
          <span>▤ policy-2026.pdf</span>
          <span>▤ lease-renewal.pdf</span>
          <span>+6 files</span>
        </S.FileRow>
        <S.DemoForm
          onSubmit={e => {
            e.preventDefault();
            ask(query);
          }}
        >
          <input
            aria-label="Ask the sample documents"
            value={query}
            onChange={e => setQuery(e.target.value)}
            maxLength={120}
          />
          <button type="submit" aria-label="Search documents">
            ↗
          </button>
        </S.DemoForm>
        <S.DemoResult aria-live="polite">
          <span>{result.verdict ? 'ANSWER WITH EVIDENCE' : 'NO SUPPORTING PASSAGE'}</span>
          <h4>{result.verdict?.answer ?? 'I do not have a passage for that.'}</h4>
          {result.verdict && (
            <p>
              ▤ {result.verdict.file} · p.{result.verdict.page}
              <br />
              <q>{result.verdict.mark}</q>
            </p>
          )}
        </S.DemoResult>
        <S.Controls aria-label="Example questions">
          <button onClick={() => ask('When does my insurance expire?')}>Ask about insurance</button>
          <button onClick={() => ask('What is my wifi password?')}>
            Try an unanswerable question
          </button>
        </S.Controls>
      </S.DemoBody>
    </S.Demo>
  );
}

export function RadarDemo() {
  const [filter, setFilter] = useState<'all' | 'gold' | 'teal'>('all');
  return (
    <S.Demo $dark aria-label="IMG Radar interactive sample">
      <S.DemoBar>
        <span>IMG RADAR / US RESIDENCY</span>
        <span>Published snapshot</span>
      </S.DemoBar>
      <S.MapWrap>
        <svg
          viewBox="0 0 1000 620"
          role="img"
          aria-label={`${tierTotals[filter]} programs in the selected category`}
        >
          {xs.map((x, i) => (
            <circle
              key={i}
              cx={x}
              cy={ys[i]}
              r={filter === 'all' ? 2.5 : 3}
              fill={tiers[i] === 2 ? '#e8c875' : tiers[i] === 1 ? '#73b6a4' : '#89938f'}
              opacity={
                filter === 'all' || (filter === 'gold' ? tiers[i] === 2 : tiers[i] === 1)
                  ? 0.85
                  : 0.06
              }
            />
          ))}
        </svg>
        <S.MapCount aria-live="polite">
          <strong>{tierTotals[filter].toLocaleString('en-US')}</strong>
          <span>
            {filter === 'all'
              ? 'programs mapped'
              : filter === 'gold'
              ? '25%+ foreign-trained teams'
              : 'evidence of IMG acceptance'}
          </span>
        </S.MapCount>
      </S.MapWrap>
      <S.Controls aria-label="Filter residency programs">
        {(['all', 'gold', 'teal'] as const).map((key, i) => (
          <button key={key} aria-pressed={filter === key} onClick={() => setFilter(key)}>
            {['All programs', '25%+ foreign-trained', 'Acceptance evidence'][i]}
          </button>
        ))}
      </S.Controls>
    </S.Demo>
  );
}

export function ChorieDemo() {
  const [text, setText] = useState(CANONICAL[0].sentence.en);
  const [result, setResult] = useState(() => parseSentence(text));
  const [confirmed, setConfirmed] = useState(false);
  return (
    <S.Demo aria-label="HomeChores interactive sample">
      <S.DemoBar>
        <span>chorie / family assistant</span>
        <span>Sample household</span>
      </S.DemoBar>
      <S.DemoBody>
        <S.FileRow>
          <img src="/assets/brand/chorie-avatar.webp" alt="Chorie" />
          <span>One message. A plan for the family.</span>
        </S.FileRow>
        <S.DemoForm
          onSubmit={e => {
            e.preventDefault();
            setResult(parseSentence(text));
            setConfirmed(false);
          }}
        >
          <input
            aria-label="Message to Chorie"
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={140}
          />
          <button type="submit" aria-label="Create draft tasks">
            ↗
          </button>
        </S.DemoForm>
        <S.TaskList aria-live="polite">
          {result.cards.length ? (
            result.cards.map((card, i) => (
              <div key={`${card.title}-${i}`}>
                <span>{confirmed ? '✓' : '+'}</span>
                <div>
                  <strong>{cardTitle(card, 'en')}</strong>
                  <small>
                    {card.kind}
                    {card.weekday !== undefined
                      ? ` · ${
                          [
                            'Sunday',
                            'Monday',
                            'Tuesday',
                            'Wednesday',
                            'Thursday',
                            'Friday',
                            'Saturday',
                          ][card.weekday]
                        }`
                      : ''}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <p>Try “Buy milk and pay the electric bill Friday”.</p>
          )}
        </S.TaskList>
        {result.cards.length > 0 && (
          <S.Controls>
            <button disabled={confirmed} onClick={() => setConfirmed(true)}>
              {confirmed ? 'Added to the sample household ✓' : 'Confirm these tasks'}
            </button>
          </S.Controls>
        )}
      </S.DemoBody>
    </S.Demo>
  );
}

export function LeadsDemo() {
  const [trade, setTrade] = useState<Trade>('ROOFING');
  return (
    <S.Demo $dark aria-label="radar407 sample leads">
      <S.DemoBar>
        <span>radar407 / daily digest</span>
        <span>Central Florida</span>
      </S.DemoBar>
      <S.DemoBody>
        <S.Controls aria-label="Filter sample leads">
          {(['ROOFING', 'HVAC', 'PLUMBING'] as const).map(t => (
            <button key={t} aria-pressed={trade === t} onClick={() => setTrade(t)}>
              {t}
            </button>
          ))}
        </S.Controls>
        <S.LeadList aria-live="polite">
          {r407Sample
            .filter(row => row.trade === trade)
            .slice(0, 3)
            .map(row => (
              <div key={row.address}>
                <span>{row.type}</span>
                <strong>{row.address}</strong>
                <small>
                  {row.county} · {row.resolved ? 'License matched' : 'License unresolved'}
                </small>
              </div>
            ))}
        </S.LeadList>
        <S.DemoCaption>
          Synthetic examples. Filtered by trade, ready for the next call.
        </S.DemoCaption>
      </S.DemoBody>
    </S.Demo>
  );
}

export function SaidaDemo() {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: 'Establish your departure date',
      text: 'Your departure date sets the timeline for the filing process.',
    },
    {
      title: 'Review income and obligations',
      text: 'Organize the information needed for your departure declaration.',
    },
    {
      title: 'Prepare the filing',
      text: 'Follow the guided workflow to prepare your tax departure documents.',
    },
  ];
  return (
    <S.Demo aria-label="Saída Certa workflow preview">
      <S.DemoBar>
        <span>saída certa / departure workflow</span>
        <span>Product preview</span>
      </S.DemoBar>
      <S.DemoBody>
        <S.StepTrack aria-label="Preview steps">
          {steps.map((s, i) => (
            <button
              key={s.title}
              aria-label={s.title}
              aria-pressed={step === i}
              onClick={() => setStep(i)}
            >
              {i < step ? '✓' : `0${i + 1}`}
            </button>
          ))}
        </S.StepTrack>
        <S.DemoResult aria-live="polite">
          <span>STEP {step + 1} OF 3</span>
          <h4>{steps[step].title}</h4>
          <p>{steps[step].text}</p>
        </S.DemoResult>
        <S.Controls>
          <button onClick={() => setStep((step + 1) % 3)}>
            {step === 2 ? 'Back to the start' : 'Next step →'}
          </button>
        </S.Controls>
      </S.DemoBody>
    </S.Demo>
  );
}
